import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import type { PartnerAllocationHistoryItem } from "Order/Utilities/partnerAllocationHistory";
import { usePartnerAllocationHistoryQuery } from "Partner/Api/Query/usePartnerAllocationHistoryQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import {
	Box,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useMemo } from "react";

type PartnerAllocationHistoryWidgetProps = {
	partnerId: number;
};

function formatSource(source: PartnerAllocationHistoryItem["source"]) {
	return source
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function formatReason(reason: string) {
	return reason
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

type AllocationStats = {
	total: number;
	accepted: number;
	rejected: number;
	rejectionRate: number;
};

function computeStats(
	allocations: PartnerAllocationHistoryItem[],
): AllocationStats {
	const total = allocations.length;
	const rejected = allocations.filter(
		(a) => a.reason === "partner_rejected_assignment",
	).length;
	const accepted = allocations.filter(
		(a) =>
			a.isCurrent === true ||
			a.reason === "auto_offer_accepted" ||
			a.reason === "manual_assignment",
	).length;
	const rejectionRate = total > 0 ? (rejected / total) * 100 : 0;

	return { total, accepted, rejected, rejectionRate };
}

export function PartnerAllocationHistoryWidget({
	partnerId,
}: PartnerAllocationHistoryWidgetProps) {
	const { formatDateTime } = useDateFormat();
	const { data: allocationHistory = [], isLoading } =
		usePartnerAllocationHistoryQuery(partnerId);

	const sortedAllocationHistory = useMemo(
		() =>
			[...allocationHistory].sort((a, b) =>
				b.assignedAt.localeCompare(a.assignedAt),
			),
		[allocationHistory],
	);

	const stats = useMemo(
		() => computeStats(allocationHistory),
		[allocationHistory],
	);

	return (
		<BaseCard title="Allocation History" isLoading={isLoading}>
			{isLoading ? (
				<></>
			) : sortedAllocationHistory.length === 0 ? (
				<Typography color="text.secondary">
					No allocation history for this partner.
				</Typography>
			) : (
				<>
					<Box display="flex" gap={3} mb={2}>
						<Typography variant="body2">
							Total: <strong>{stats.total}</strong>
						</Typography>
						<Typography variant="body2">
							Accepted: <strong>{stats.accepted}</strong>
						</Typography>
						<Typography variant="body2">
							Rejected: <strong>{stats.rejected}</strong>
						</Typography>
						<Typography variant="body2">
							Rejection rate: <strong>{stats.rejectionRate.toFixed(1)}%</strong>
						</Typography>
					</Box>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Order</TableCell>
									<TableCell>Assigned at</TableCell>
									<TableCell>Unassigned at</TableCell>
									<TableCell>Source</TableCell>
									<TableCell>Reason</TableCell>
									<TableCell>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedAllocationHistory.map((allocation) => {
									const isCurrent =
										allocation.isCurrent === true ||
										allocation.unassignedAt === null;

									return (
										<TableRow key={allocation.id}>
											<TableCell>#{allocation.orderId}</TableCell>
											<TableCell>
												{formatDateTime(allocation.assignedAt)}
											</TableCell>
											<TableCell>
												{allocation.unassignedAt
													? formatDateTime(allocation.unassignedAt)
													: "-"}
											</TableCell>
											<TableCell>{formatSource(allocation.source)}</TableCell>
											<TableCell>
												{allocation.reason
													? formatReason(allocation.reason)
													: "-"}
											</TableCell>
											<TableCell>
												<Chip
													size="small"
													color={isCurrent ? "success" : "default"}
													label={isCurrent ? "Current" : "Past"}
												/>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}
		</BaseCard>
	);
}
